import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

/**
 * Splits a single <p> block that contains DOUBLE <br><br> sequences into
 * separate <p> blocks.  Single <br> within a paragraph is kept as-is (it
 * represents a hard line-break, e.g. an address or press-release header).
 * Preserves any outer <span> wrapper (e.g. the review-red colour mark).
 * Returns the input unchanged when no paragraph-level splitting is needed.
 */
export function normalizeParagraphBreaks(html: string): string {
  if (!html || !html.includes('<br')) return html;

  const doubleBr = /<br\s*\/?>\s*<br\s*\/?>/i;

  return html.replace(/<p([^>]*)>([\s\S]*?)<\/p>/g, (match, pAttrs, inner) => {
    if (!doubleBr.test(inner)) return match;

    // Check whether the entire inner content is wrapped in a single <span>
    const spanMatch = inner.match(/^(<span[^>]*>)([\s\S]*)(<\/span>)$/);

    if (spanMatch) {
      const [, spanOpen, spanInner, spanClose] = spanMatch;
      const parts = spanInner.split(/<br\s*\/?>\s*<br\s*\/?>/gi)
        .map(s => s.replace(/^(<br\s*\/?>)+/i, '').replace(/(<br\s*\/?>)+$/i, '').trim())
        .filter(Boolean);
      if (parts.length <= 1) return match;
      return parts.map(part => `<p${pAttrs}>${spanOpen}${part}${spanClose}</p>`).join('');
    }

    // No outer span — split on double-<br> only
    const parts = inner.split(/<br\s*\/?>\s*<br\s*\/?>/gi)
      .map(s => s.replace(/^(<br\s*\/?>)+/i, '').replace(/(<br\s*\/?>)+$/i, '').trim())
      .filter(Boolean);
    if (parts.length <= 1) return match;
    return parts.map(part => `<p${pAttrs}>${part}</p>`).join('');
  });
}

/**
 * Converts plain text to HTML <p> blocks.
 * - If the text already contains block-level HTML tags it is normalised
 *   (converting <br>-separated lines inside a single <p> into separate <p>
 *   blocks) and returned.
 * - Splits on double newlines (\n\n) first; if none are found falls back to
 *   single newlines so that older imported content (stored with one \n per
 *   paragraph) still gets proper <p> separation rather than a single block.
 */
export function plainTextToHtml(text: string): string {
  if (!text) return '';
  if (/<(p|div|h[1-6]|ul|ol|li|blockquote)\b/i.test(text)) return normalizeParagraphBreaks(text);
  const doubleNewlineParts = text.split(/\n\n+/).map(p => p.trim()).filter(Boolean);
  const paragraphs = doubleNewlineParts.length > 1
    ? doubleNewlineParts
    : text.split(/\n/).map(p => p.trim()).filter(Boolean);
  if (paragraphs.length === 0) return '';
  return paragraphs
    .map(p => `<p>${p}</p>`)
    .join('\n');
}

/**
 * Wraps each paragraph's inline content in a red colour span so new articles
 * load as "unreviewed" (red = not yet edited) in the Tiptap editor.
 *
 * If the HTML already contains any inline color style marks the content is
 * returned as-is — meaning previously-reviewed articles keep their saved state.
 * Empty / whitespace-only paragraphs and <br> placeholders are left unwrapped.
 */
export function wrapInReviewRed(html: string): string {
  if (!html) return html;
  if (html.includes('style="color:') || html.includes("style='color:")) return html;
  return html.replace(/<p([^>]*)>([\s\S]*?)<\/p>/g, (_, attrs, inner) => {
    const trimmed = inner.trim();
    if (!trimmed || trimmed === '<br>' || trimmed === '<br/>') return `<p${attrs}>${inner}</p>`;
    return `<p${attrs}><span style="color:#dc2626">${inner}</span></p>`;
  });
}
